// @flow
import type { Action } from 'app/common/redux/types';
import type { BoardAccess } from 'app/packages/pinterest-flow-types/board';
import type { BoardPrivacyType } from 'app/common/lib/Const';
import type { ContentSensitivity } from 'app/packages/pinterest-flow-types/contentSensitivity';
import type { ImagePositionType } from 'app/common/react/components/profile/ProfileBoardCard/commonTypes';
import { UserActionTypes } from 'app/common/redux/actions/user/actionTypes';

export type SummaryBoard = {
  access: BoardAccess,
  collaborated_by_me?: boolean,
  id: string,
  image_cover_url: string,
  is_collaborative?: boolean,
  followed_by_me?: boolean,
  name: string,
  owner: {
    id: string,
  },
  pin_count: number,
  privacy?: BoardPrivacyType,
  section_count?: number,
  sensitivity?: ContentSensitivity,
  should_show_board_activity: boolean,
  type: 'board',
  url: string,
};

// $FlowIssue always need to & an object, even an empty {} :NOTE(yansun)
export type State = SummaryBoard & {
  archived_by_me_at: string,
  cover_pin?: {
    custom_cover: boolean,
    scale: number,
    pin_id: string,
    crop: ImagePositionType,
    image_url: string,
  },
};

export default function boardInfoReducer(state: State = {}, action: Action): State {
  if (action.type === 'RESOURCE_FETCH_COMPLETE' || action.type === 'RESOURCE_FETCH_MORE_COMPLETE') {
    const { payload } = action;
    if (
      payload.name === 'BoardResource' ||
      payload.name === 'BoardPageResource' ||
      payload.name === 'BoardActionStatusResource'
    ) {
      return {
        ...state,
        ...(payload.data || {}),
        cover_images: {
          ...state.cover_images,
          ...(payload.data || {}).cover_images,
        },
        images: {
          ...state.images,
          ...(payload.data || {}).images,
        },
      };
    }
  } else if (action.type === 'BOARD_INVITE_ACCEPTED_BY_VIEWER') {
    const { boardId } = action.payload;
    return {
      ...state,
      id: boardId,
      access: ['write'],
      collaborated_by_me: true,
      is_collaborative: true,
    };
  } else if (action.type === 'BOARD_FOLLOW') {
    const { boardId, value } = action.payload;
    return {
      ...state,
      id: boardId,
      followed_by_me: value,
    };
  } else if (action.type === 'BOARD_COVER_IMAGE_UPDATED') {
    const { boardId, coverPin } = action.payload;
    return {
      ...state,
      id: boardId,
      cover_pin: coverPin,
    };
  } else if (action.type === UserActionTypes.USER_FOLLOW) {
    const { id: ownerId, value } = action.payload;
    if (!state.owner || ownerId !== state.owner.id) {
      return state;
    }

    return {
      ...state,
      followed_by_me: value,
    };
  } else if (action.type === 'SECTION_ADDED') {
    const { section_count } = state || {};
    return {
      ...state,
      section_count: (section_count || 0) + 1,
    };
  } else if (action.type === 'SECTION_DELETED') {
    const { section_count } = state || {};
    return {
      ...state,
      section_count: (section_count || 1) - 1,
    };
  } else if (action.type === 'BOARD_PIN_COUNT_CHANGED') {
    const { pin_count, sectionless_pin_count } = state || {};
    const { sectionId } = action.payload;
    const increment = action.type === 'BOARD_PIN_COUNT_CHANGED' ? action.payload.increment : 1;
    return {
      ...state,
      pin_count: typeof pin_count === 'number' ? Math.max(0, pin_count + increment) : pin_count,
      sectionless_pin_count:
        !sectionId && typeof sectionless_pin_count === 'number'
          ? Math.max(0, sectionless_pin_count + increment)
          : sectionless_pin_count,
    };
  } else if (action.type === 'BOARD_ARCHIVED') {
    return { ...state, archived_by_me_at: new Date().toString() };
  } else if (action.type === 'BOARD_UNARCHIVED') {
    const nextState = { ...state };
    delete nextState.archived_by_me_at;
    return nextState;
  } else if (action.type === 'BOARD_REQUEST_TO_JOIN') {
    const { boardId, value } = action.payload;
    return {
      ...state,
      id: boardId,
      viewer_collaborator_join_requested: value,
    };
  } else if (action.type === 'BOARD_UPDATE_COLLABORATION_SETTINGS') {
    const { boardId, collaboratorRequestsEnabled } = action.payload;
    return {
      ...state,
      id: boardId,
      collaborator_requests_enabled: collaboratorRequestsEnabled,
    };
  } else if (action.type === 'BOARD_DISABLE_MORE_IDEAS_BADGING') {
    return {
      ...state,
      has_fresh_more_ideas_tab: false,
    };
  }

  return state;
}
